import { AxiosRequestSender } from '@vue-storefront/sdk-axios-request-sender';
import axios from 'axios';
import consola from 'consola';

class Config extends Map {
    // eslint-disable-next-line no-useless-constructor
    constructor() {
        super();
    }
    get(key) {
        return super.get(key);
    }
    set(key, value) {
        super.set(key, value);
        return this;
    }
}
const config = new Config();

/**
 * Resolve base URL based on the client-side or server-side environment.
 * Fallbacks to `apiUrl` when there is no `ssrApiUrl` on server-side environment,
 * bacause `ssrApiUrl` is optional.
 */
const resolveBaseUrl = () => {
    const apiUrl = config.get("apiUrl");
    const ssrApiUrl = config.get("ssrApiUrl");
    const baseUrl = typeof window === "undefined" ? ssrApiUrl || apiUrl : apiUrl;
    return baseUrl;
};

/**
 * This is a proxy that will be used to create an axios instance.
 * It will be used to set the base url and withCredentials flag.
 * Depending on the client-side or server side usage, the base url will be either the `apiUrl` or the `ssrApiUrl`.
 * `resolveBaseUrl` is taking care of that.
 */
const client = new Proxy(axios.create(), {
    get: (axiosInstance, property) => {
        axiosInstance.defaults.withCredentials = true;
        axiosInstance.defaults.baseURL = resolveBaseUrl();
        return axiosInstance[property];
    },
});

/**
 * Fetches content from Contentstack.
 *
 * @remarks
 * This method uses [Contentstack Delivery API](https://www.contentstack.com/docs/developers/apis/content-delivery-api/) to fetch content.
 *
 * @param {GetContentParams} props - Params for fetching content.
 *
 * @returns {Promise<any[]>} - Contentstack content.
 *
 */
async function getContent(props) {
    return new AxiosRequestSender(client)
        .setUrl("getContent")
        .setMethod("GET")
        .setProps(props)
        .send();
}

var methods = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getContent: getContent
});

/**
 * Initialize the Contentstack connector.
 */
const contentstackConnector = (options) => {
    config.set("apiUrl", options.apiUrl);
    config.set("ssrApiUrl", options.ssrApiUrl);
    return methods;
};

/**
 * Maps Contentstack image object to common image object.
 * @param image Contentstack image object.
 * @returns Common image object.
 */
const getImageObject = (image) => ({
    url: image.url,
    alt: image.title,
    metadata: image,
});

const extractNestedItem = (item) => (item.item ? item.item : item);
const extractNestedProps = (item) => (item.props ? item.props : item);
const filterProps = [
    "component",
    "_in_progress",
    "_version",
    "ACL",
    "publish_details",
    "tags",
    "created_at",
    "created_by",
    "updated_at",
    "updated_by",
    "_metadata",
];
/**
 * Extracts components from Contentstack items array as common components array.
 * @param items Contentstack items array.
 * @returns Mapped components array.
 */
const extractComponents = (items) => {
    if (!items || !items.length) {
        consola.error("No content items provided for render");
        return [];
    }
    return items.map((item) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { $, ...itemComponent } = item;
        const data = {
            ...(Object.values(itemComponent).length === 1
                ? Object.values(itemComponent)[0]
                : item),
        };
        const componentName = data.component === "Reference" ? "RenderContent" : data.component;
        return {
            componentName,
            props: ((data) => {
                filterProps.forEach((prop) => delete data[prop]);
                if (data.image) {
                    data.image = getImageObject(data.image);
                }
                if (data.images && Array.isArray(data.images)) {
                    data.images = data.images.map(getImageObject);
                }
                if (data.items && Array.isArray(data.items)) {
                    data.items = data.items.map(extractNestedItem);
                }
                if (data.props) {
                    data = {
                        ...data,
                        ...extractNestedProps(data),
                    };
                    delete data.props;
                }
                if (data.$) {
                    data.live_preview = data.$;
                    delete data.$;
                }
                if (data.component) {
                    delete data.component;
                }
                return data;
            })(data),
        };
    });
};

var utils = /*#__PURE__*/Object.freeze({
  __proto__: null,
  extractComponents: extractComponents,
  getImageObject: getImageObject
});

/**
 * Contentstack module.
 */
const contentstackModule = (options) => ({
    connector: contentstackConnector({
        apiUrl: options.apiUrl,
        ssrApiUrl: options.ssrApiUrl,
    }),
    utils,
    subscribers: {},
});

export { client, contentstackModule };
//# sourceMappingURL=index.es.js.map
